// React
import { useState } from "react";

// Redux
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

// React Query
import { useMutation, useQuery } from "@tanstack/react-query";

// MUI
import { Grid, Stack } from "@mui/material";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// API
import { getTimecard, ingestManualTimecards } from "@DataPages/api/timeAndPayRates";

// Helpers
import { getPositionDepartment } from "@Common/helpers/Positions";
import { or } from "@/Common/helpers/sonar";
import { getLocalDate } from "@/Common/helpers/Dates";
import { isStatusCompleted } from "@/Common/helpers/PayPeriods";

// Components
import CountDownButton from "@Layouts/components/Buttons/CountDownButton";
import TextIconMessage from "@Layouts/components/Basics/TextIconMessage";
import TimeCardsContent from "./TimeCardsContent";

// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";
import {
	IDepartment,
	IPayPeriodTimesCard,
	IUserTimeRecordToShow,
	ITimeCard,
	IPositionRecords,
} from "@/DataPages/types/timeAndPayRates/models";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const matchDepartments = (positions: string[], timeAndPayRates: IPayPeriodTimesCard[]) => {
	if (!timeAndPayRates?.length || !positions?.length) {
		return [];
	}

	const departments = new Map();
	const information = mergeUsersRecords(timeAndPayRates);
	const createdAt = timeAndPayRates.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0].createdAt;
	const updatedAt = timeAndPayRates.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())[0].updatedAt;
	positions.forEach((position) => {
		const department = getPositionDepartment(position);

		const stored = departments.get(department) || {
			department,
			listPositions: [],
			createdAt: createdAt,
			updatedAt: updatedAt,
		};
		const records = getPositionRecords(position, information);
		if (records?.length) {
			const flattenedRecords: IUserTimeRecordToShow[] = [];
			records.forEach((record) => {
				record.timecard.forEach((timecard: IUserTimeRecordToShow) => {
					flattenedRecords.push({
						...timecard,
						fullName: record.fullName,
					});
				});
			});

			stored.listPositions.push({
				position,
				records: flattenedRecords,
			});

			departments.set(department, stored);
		}
	});

	return Array.from(departments.values());
};

const getPositionRecords = (pos: string, records: IPositionRecords[] = []) => {
	return records.reduce((results: Omit<IPositionRecords, "position">[], record) => {
		if (record.position === pos) {
			const { position, ...rest } = record;
			results.push(rest);
		}
		return results;
	}, []);
};

const mergeUsersRecords = (usersTimeAndPayRates: IPayPeriodTimesCard[] = []) => {
	const usersMap = new Map();
	usersTimeAndPayRates.forEach((element) => {
		const user = usersMap.get(element.userId) || {
			_id: element.userId,
			fullName: element.fullName,
			position: element.position,
			timecard: [],
		};

		if (element.timecards) {
			element.timecards.forEach((tc: ITimeCard, index: number) => {
				const key = `${user._id}-${index}`;
				user.timecard.push({
					...tc,
					fullName: element.fullName,
					hierarchy: index === 0 ? [key] : [`${user._id}-0`, `child_${user._id}-${index}`],
					_id: index === 0 ? key : `child_${user._id}-${index}`,
				});
			});
		}

		usersMap.set(element.userId, user);
	});

	return Array.from(usersMap.values());
};

const getLastUpdatedDate = (date: string, timezone: string | undefined, dateFormat = DATE_FORMAT.usDateTime24hSeconds) => {
	if (date) {
		return getLocalDate(date, dateFormat, timezone) ?? "";
	}

	return null;
};

interface ITimesCardsBodyProps {
	selected: IPeriodTreeSelection;
	managedPositions: string[];
	isUpdatable: boolean;
}

const TimeCardsBody = (props: ITimesCardsBodyProps) => {
	const { selected, managedPositions, isUpdatable } = props;
	const [updatedAt, setUpdatedAt] = useState<string | null>(null);
	const [ingesting, setIngesting] = useState(false);
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId ?? "";
	// Subscription
	const { getSubscription } = useSubscriptions();
	const timeAndPayRatesSubscription = getSubscription(["timeAndPayRates", selected?.payPeriodId]);

	const {
		data: timecards,
		isLoading: isLoadingTimecards,
		refetch,
	} = useQuery({
		queryKey: ["timecard", locationId, selected?.cycleId, selected?.payPeriodId],
		queryFn: async () => {
			const response = await getTimecard(locationId, selected?.cycleId, selected?.payPeriodId);
			const departments = matchDepartments(managedPositions, response ?? []);
			if (departments && departments.length > 0) {
				setUpdatedAt(departments[0].updatedAt);
			}
			return departments as IDepartment[];
		},

		enabled: or(!!managedPositions, !!locationId, !!selected?.payPeriodId),
		staleTime: Infinity,
	});

	const { mutate: ingestData } = useMutation({
		mutationFn: async () => {
			const areValid = or(!!locationId, !!selected, !!selected?.payPeriodId);
			if (!areValid) {
				return false;
			}
			setIngesting(true);
			await ingestManualTimecards(locationId, selected?.cycleId, selected?.payPeriodId);
			return true;
		},
	});

	timeAndPayRatesSubscription?.subscriptions?.onUpdateTimecard?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateTimecard", IPayPeriodTimesCard>) => {
			const updatedTimecard: IPayPeriodTimesCard | undefined = result.data?.onUpdateTimecard;
			if (!updatedTimecard) {
				return null;
			}
			setIngesting(false);
			return refetch();
		}
	);

	const isLoading = isLoadingTimecards || ingesting;

	return (
		<Stack className="time-rate-container">
			<TimeCardsContent timeCards={timecards} isLoading={isLoading} selected={selected} />
			<RenderButton
				updatedAt={updatedAt}
				isLoading={isLoading}
				ingestData={ingestData}
				data={timecards}
				isUpdatable={isUpdatable}
				disabled={isStatusCompleted(selected?.status ?? "")}
			/>
		</Stack>
	);
};

export default TimeCardsBody;

interface IRenderButtonProps {
	updatedAt: string | null;
	isLoading: boolean;
	ingestData: () => void;
	data: IDepartment[] | undefined;
	isUpdatable: boolean;
	disabled: boolean;
}

const RenderButton = (props: IRenderButtonProps) => {
	const { updatedAt, isLoading, isUpdatable, data, disabled, ingestData } = props;
	const timezone = useSelector((state: RootState) => state.location.location?.timezone);
	return (
		<Grid className="time-rate-bottomGrid">
			<Stack direction={"row"} alignItems={"flex-start"}>
				<TextIconMessage
					icon
					title={"Last Update"}
					content={updatedAt ? getLastUpdatedDate(updatedAt, timezone, DATE_FORMAT.usDateTime12hUnpadded) : ""}
					showContent={Boolean(data?.length)}
					loading={isLoading}
					sx={{
						marginRight: "10px",
						color: "var(--text-color)",
					}}
				/>
				{isUpdatable && (
					<CountDownButton
						typographySx={{ fontSize: "12px" }}
						initialCopy={"Update Now"}
						actionCopy={"Updating..."}
						loading={isLoading}
						counter={60}
						onClickCallBack={ingestData}
						disabled={disabled}
					/>
				)}
			</Stack>
		</Grid>
	);
};
